import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../../services/api';

const BulkReportsGenerator = () => {
  const [funds, setFunds] = useState([]);
  const [totalFunds, setTotalFunds] = useState(0);
  const [selectedFunds, setSelectedFunds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobProgress, setJobProgress] = useState(0);
  const [jobStatus, setJobStatus] = useState(null);
  const [message, setMessage] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchFunds();

    // Cleanup polling on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const fetchFunds = async () => {
    setIsLoading(true);
    try {
      const data = await adminAPI.getBulkReportsFunds();
      setFunds(data.funds || []);
      setTotalFunds(data.total || 0);
    } catch (err) {
      console.error('Error fetching funds:', err);
      setMessage({ type: 'error', text: 'Failed to load funds with content' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedFunds(new Set(funds.map(f => f.apir_code)));
    } else {
      setSelectedFunds(new Set());
    }
  };

  const handleSelectFund = (apirCode, checked) => {
    const newSelected = new Set(selectedFunds);
    if (checked) {
      newSelected.add(apirCode);
    } else {
      newSelected.delete(apirCode);
    }
    setSelectedFunds(newSelected);
  };

  const pollJobStatus = async (jobId) => {
    try {
      const status = await adminAPI.getBulkReportJobStatus(jobId);
      setJobProgress(status.progress || 0);
      setJobStatus(status.status);

      if (status.status === 'completed') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        // Download the file
        await downloadReport(jobId, status.generated, status.failed);

      } else if (status.status === 'failed') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        setIsGenerating(false);
        setJobId(null);
        setMessage({
          type: 'error',
          text: `Report generation failed: ${status.error || 'Unknown error'}`
        });
      }
      // If still running/pending, continue polling
    } catch (err) {
      console.error('Error polling job status:', err);
      // Stop polling on 404 (job lost, e.g. server restart during generation)
      if (err.response?.status === 404) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsGenerating(false);
        setJobId(null);
        setMessage({
          type: 'error',
          text: 'Job lost — the server may have restarted during generation. Please try again.'
        });
      }
    }
  };

  const downloadReport = async (jobId, generated, failed) => {
    try {
      const response = await adminAPI.downloadBulkReportJob(jobId);

      // Create download link for the ZIP file
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Get filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `fund_reports_${new Date().toISOString().split('T')[0]}.zip`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setIsGenerating(false);
      setJobId(null);
      setJobProgress(0);

      if (failed > 0) {
        setMessage({
          type: 'warning',
          text: `Generated ${generated} reports successfully. ${failed} failed.`
        });
      } else {
        setMessage({
          type: 'success',
          text: `Successfully generated ${generated} reports! Download started.`
        });
      }
    } catch (err) {
      console.error('Error downloading report:', err);
      setIsGenerating(false);
      setJobId(null);
      setMessage({ type: 'error', text: 'Failed to download reports' });
    }
  };

  const handleGenerateReports = async () => {
    if (totalFunds === 0) {
      setMessage({ type: 'error', text: 'No funds with content to generate reports for' });
      return;
    }

    // Use selected funds or all funds if none selected
    const apirCodes = selectedFunds.size > 0 ? Array.from(selectedFunds) : null;
    const reportCount = selectedFunds.size > 0 ? selectedFunds.size : totalFunds;

    setIsGenerating(true);
    setJobProgress(0);
    setJobStatus('pending');
    setMessage({ type: 'info', text: `Starting report generation for ${reportCount} funds...` });

    try {
      // Start the background job
      const response = await adminAPI.generateBulkReports(apirCodes);
      setJobId(response.job_id);

      setMessage({
        type: 'info',
        text: `Generating ${reportCount} reports in background. Progress will update automatically.`
      });

      // Start polling for status every 2 seconds
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(response.job_id);
      }, 2000);

    } catch (err) {
      console.error('Error starting report generation:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to start report generation';
      setMessage({ type: 'error', text: errorMessage });
      setIsGenerating(false);
    }
  };

  const handlePreview = async (apirCode) => {
    setPreviewLoading(true);
    try {
      const data = await adminAPI.previewFundReport(apirCode);
      setPreviewData(data);
    } catch (err) {
      console.error('Error previewing report:', err);
      setMessage({ type: 'error', text: `Failed to preview report for ${apirCode}` });
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewData(null);
  };

  const isAllSelected = funds.length > 0 && selectedFunds.size === funds.length;
  const isSomeSelected = selectedFunds.size > 0 && selectedFunds.size < funds.length;

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bulk-reports-generator">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-1">Bulk Report Generator</h4>
          <p className="text-muted mb-0">
            Select funds to generate reports, or generate all at once.
          </p>
        </div>
        <button
          className="btn btn-primary btn-lg"
          onClick={handleGenerateReports}
          disabled={isGenerating || totalFunds === 0}
        >
          {isGenerating ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status"></span>
              Generating... {jobProgress}%
            </>
          ) : (
            <>
              <i className="bi bi-download me-2"></i>
              {selectedFunds.size > 0
                ? `Generate Selected (${selectedFunds.size})`
                : `Generate All Reports (${totalFunds})`
              }
            </>
          )}
        </button>
      </div>

      {/* Progress Bar */}
      {isGenerating && (
        <div className="mb-4">
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">
              {jobStatus === 'pending' && 'Starting...'}
              {jobStatus === 'running' && 'Generating reports...'}
              {jobStatus === 'completed' && 'Preparing download...'}
            </small>
            <small className="text-muted">{jobProgress}%</small>
          </div>
          <div className="progress" style={{ height: '10px' }}>
            <div
              className="progress-bar progress-bar-striped progress-bar-animated"
              role="progressbar"
              style={{ width: `${jobProgress}%` }}
              aria-valuenow={jobProgress}
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
        </div>
      )}

      {/* Selection Summary */}
      {selectedFunds.size > 0 && !isGenerating && (
        <div className="alert alert-primary d-flex justify-content-between align-items-center">
          <span>
            <strong>{selectedFunds.size}</strong> of {totalFunds} funds selected
          </span>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => setSelectedFunds(new Set())}
          >
            Clear Selection
          </button>
        </div>
      )}

      {message && (
        <div
          className={`alert alert-${
            message.type === 'error' ? 'danger' :
            message.type === 'warning' ? 'warning' :
            message.type === 'info' ? 'info' : 'success'
          } alert-dismissible`}
        >
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Preview Modal */}
      {previewData && (
        <div className="card mb-4 border-primary">
          <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Report Preview: {previewData.fund_name}</h6>
            <button className="btn btn-sm btn-light" onClick={closePreview}>Close</button>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-6">
                <h6>Fund Details</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr><td><strong>APIR Code:</strong></td><td>{previewData.apir_code}</td></tr>
                    <tr><td><strong>Asset Class:</strong></td><td>{previewData.asset_class}</td></tr>
                    <tr><td><strong>Style:</strong></td><td>{previewData.style}</td></tr>
                    <tr><td><strong>Inception:</strong></td><td>{previewData.fund_inception}</td></tr>
                    <tr><td><strong>Benchmark:</strong></td><td>{previewData.benchmark}</td></tr>
                  </tbody>
                </table>
              </div>
              <div className="col-md-6">
                <h6>Performance Statistics (3 Year)</h6>
                <table className="table table-sm">
                  <tbody>
                    <tr><td><strong>Annualised Return:</strong></td><td>{previewData.annualised_return}</td></tr>
                    <tr><td><strong>Annualised Volatility:</strong></td><td>{previewData.annualised_volatility}</td></tr>
                    <tr><td><strong>Information Ratio:</strong></td><td>{previewData.information_ratio}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
            <hr />
            <h6>Overview</h6>
            <p className="small">{previewData.overview_text || 'No overview text'}</p>
            {previewData.drummond_opinions?.length > 0 && (
              <>
                <h6>Drummond's Opinions</h6>
                <ul className="small">
                  {previewData.drummond_opinions.map((opinion, idx) => (
                    <li key={idx}>{opinion}</li>
                  ))}
                </ul>
              </>
            )}
            {previewData.portfolio_considerations?.length > 0 && (
              <>
                <h6>Portfolio Considerations</h6>
                <ul className="small">
                  {previewData.portfolio_considerations.map((consideration, idx) => (
                    <li key={idx}>{consideration}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}

      {/* Funds List */}
      {totalFunds === 0 ? (
        <div className="card">
          <div className="card-body text-center text-muted py-5">
            <i className="bi bi-file-earmark-x display-1 mb-3"></i>
            <p className="mb-3">No funds with content found in the database.</p>
            <p className="small">
              Add fund content in the "Fund Content" tab before generating reports.
            </p>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">Funds with Content ({totalFunds})</h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isAllSelected}
                      ref={input => {
                        if (input) {
                          input.indeterminate = isSomeSelected;
                        }
                      }}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="Select all funds"
                      disabled={isGenerating}
                    />
                  </th>
                  <th>APIR Code</th>
                  <th>Fund Name</th>
                  <th>Last Updated</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {funds.map((fund) => (
                  <tr
                    key={fund.apir_code}
                    className={selectedFunds.has(fund.apir_code) ? 'table-primary' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedFunds.has(fund.apir_code)}
                        onChange={(e) => handleSelectFund(fund.apir_code, e.target.checked)}
                        disabled={isGenerating}
                      />
                    </td>
                    <td>
                      <code className="small">{fund.apir_code}</code>
                    </td>
                    <td>{fund.fund_name}</td>
                    <td>
                      <small className="text-muted">
                        {fund.updated_at ? new Date(fund.updated_at).toLocaleDateString() : 'N/A'}
                      </small>
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handlePreview(fund.apir_code)}
                        disabled={previewLoading || isGenerating}
                      >
                        {previewLoading ? (
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                          'Preview'
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Information Card */}
      <div className="card mt-4 bg-light">
        <div className="card-body">
          <h6 className="card-title">
            <i className="bi bi-info-circle me-2"></i>
            About Bulk Report Generation
          </h6>
          <ul className="mb-0 small">
            <li>Select specific funds using checkboxes, or generate all at once</li>
            <li>Reports are generated in the background - you can see progress in real-time</li>
            <li>Each report includes fund details, performance data, and written commentary</li>
            <li>Reports are downloaded as a ZIP file containing HTML and PDF files</li>
            <li>Generation uses parallel processing for faster completion</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BulkReportsGenerator;
